/**
 * Typed HTTP client for stagery-api.
 *
 * Design notes:
 *   - Base URL resolved from import.meta.env.VITE_API_BASE_URL. During `vite dev`
 *     the Vite proxy forwards /api/* to that target, so relative paths work
 *     locally. In production builds, the Vite proxy is absent, so this client
 *     prefixes the absolute URL.
 *   - credentials: 'include' carries the subdomain cookie set by the API on
 *     .stagery.ai. The local-dev proxy preserves cookies.
 *   - Auth: Clerk session token sent as Authorization: Bearer <token> on every
 *     request. Falls back gracefully if Clerk is not yet loaded.
 *   - CSRF: mutation requests look for csrfToken in-memory (set by the auth
 *     context on login) and send it as X-CSRF-Token. The API's verifyCsrf
 *     middleware compares header to the csrfToken cookie (double-submit).
 *   - 429: exponential backoff (2^n * 1000ms, up to 3 retries).
 *   - 401: delegates sign-out to Clerk — no localStorage auth state.
 */

import { getClerkToken } from '../src/auth/getToken';

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
export const API_BASE: string = viteEnv?.VITE_API_BASE_URL ?? '';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

function extractErrorMessage(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null) {
    if ('message' in body) return String((body as Record<string, unknown>).message);
    if ('error' in body) return String((body as Record<string, unknown>).error);
  }
  if (typeof body === 'string' && body.length > 0) return body;
  return undefined;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    const msg = message ?? extractErrorMessage(body) ?? `API error ${status}`;
    super(msg);
    this.status = status;
    this.body = body;
  }
}

export interface ApiFetchInit extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  // Set true to skip Accept: application/json (e.g., for image fetches).
  raw?: boolean;
  // Public GETs can opt out of the Clerk token so Vercel/CDN caching remains effective.
  skipAuth?: boolean;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const isExternalUrl = path.startsWith('http') && (!API_BASE || !path.startsWith(API_BASE));
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const body = init.body === undefined ? undefined : JSON.stringify(init.body);

  const maxRetries = 3;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const token = isExternalUrl || init.skipAuth ? null : await getClerkToken();
    const headers: Record<string, string> = {
      ...(init.raw ? {} : { Accept: 'application/json' }),
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!isExternalUrl && MUTATION_METHODS.has(method) && csrfToken
        ? { 'X-CSRF-Token': csrfToken }
        : {}),
      ...init.headers,
    };

    const res = await fetch(url, {
      ...init,
      method,
      headers,
      body,
      credentials: 'include',
    });

    if (res.status === 429 && attempt < maxRetries) {
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
      attempt++;
      continue;
    }

    if (res.status === 401) {
      // Skip auto-signOut for /api/auth/me: useAuth handles this case with the
      // `profileLoadFailed` retry UI. Calling signOut here clears the Clerk
      // session before the retry UI can render, dropping the user back to
      // /sign-in — where Clerk completes the dormant flow and bounces back to
      // /, retriggering /api/auth/me → 401 → signOut → loop.
      const isAuthMeProbe = !isExternalUrl && path === '/api/auth/me';
      if (!isAuthMeProbe && token) {
        window.Clerk?.signOut?.()?.catch(() => {});
      }
      const text = await res.text();
      let payload: unknown = text;
      if (text) {
        try {
          payload = JSON.parse(text) as unknown;
        } catch {
          payload = text;
        }
      }
      throw new ApiError(res.status, payload);
    }

    if (!res.ok) {
      const text = await res.text();
      let payload: unknown = text;
      if (text) {
        try {
          payload = JSON.parse(text) as unknown;
        } catch {
          payload = text;
        }
      }
      throw new ApiError(res.status, payload);
    }

    // 204 / empty body
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('application/json')) {
      return (await res.json()) as T;
    }
    return (await res.text()) as unknown as T;
  }
}

/** Convenience helpers with typed return values. */
export const api = {
  get: <T>(path: string, init: Omit<ApiFetchInit, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
